import { createDemoPeriodComparisonMatcher } from '@web/lib/demo/utils';
import { AnalyticPeriodsEnum } from '@workalizer/shared/enums';
import { diffComparisonType, diffComparisonValueFormat } from '@workalizer/shared/types';
import { getInterval, getPercentageDiff, getPreviousPeriod, groupByPeriod, roundToTwoDecimals } from '@workalizer/shared/utils';
import { addDays, differenceInDays, endOfDay, format, startOfDay } from 'date-fns';
var generateMockData = function (dateFrom, dateTo) {
    var startDate = new Date(dateFrom);
    var endDate = new Date(dateTo);
    var days = differenceInDays(endDate, startDate) + 1;
    return Array.from({ length: days }, function (_, i) {
        var currentDate = addDays(startDate, i);
        var dateStr = format(currentDate, 'yyyy-MM-dd');
        var officeHours = 4 + (i % 3) + Math.random() * 2;
        var remoteHours = 2 + (i % 2) + Math.random() * 1.5;
        return {
            date: dateStr,
            items: [
                {
                    officeHours: roundToTwoDecimals(officeHours),
                    remoteHours: roundToTwoDecimals(remoteHours)
                }
            ]
        };
    });
};
export var workLocationBreakdown = function (input) {
    var _a = getPreviousPeriod(input.dateFrom, input.dateTo), prevPeriodDateFrom = _a.prevPeriodDateFrom, prevPeriodDateTo = _a.prevPeriodDateTo;
    var rawInterval = getInterval(input.dateFrom, input.dateTo);
    var interval = rawInterval === AnalyticPeriodsEnum.HOUR
        ? AnalyticPeriodsEnum.DAY
        : rawInterval;
    var currentDailyData = generateMockData(input.dateFrom, input.dateTo);
    var previousDailyData = generateMockData(prevPeriodDateFrom, prevPeriodDateTo);
    var currentPeriods = groupByPeriod(currentDailyData, startOfDay(new Date(input.dateFrom)), endOfDay(new Date(input.dateTo)), interval);
    var previousPeriods = groupByPeriod(previousDailyData, startOfDay(new Date(prevPeriodDateFrom)), endOfDay(new Date(prevPeriodDateTo)), interval);
    var periodMatcher = createDemoPeriodComparisonMatcher(input, interval, currentPeriods, previousPeriods);
    var periods = currentPeriods.map(function (period, idx) {
        var _a, _b, _c;
        var currentItems = ((_a = period.items) === null || _a === void 0 ? void 0 : _a.flat()) || [];
        var prevPeriod = periodMatcher.getItem(idx, previousPeriods);
        var prevItems = ((_b = prevPeriod === null || prevPeriod === void 0 ? void 0 : prevPeriod.items) === null || _b === void 0 ? void 0 : _b.flat()) || [];
        var currentOfficeHours = currentItems.reduce(function (sum, item) { return sum + item.officeHours; }, 0) || 0;
        var currentRemoteHours = currentItems.reduce(function (sum, item) { return sum + item.remoteHours; }, 0) || 0;
        var previousOfficeHours = prevItems.reduce(function (sum, item) { return sum + item.officeHours; }, 0) || 0;
        var previousRemoteHours = prevItems.reduce(function (sum, item) { return sum + item.remoteHours; }, 0) || 0;
        var teamAverageOfficeHours = input.userId !== null
            ? roundToTwoDecimals(currentOfficeHours * 0.9 + Math.random() * 0.5)
            : null;
        var teamAverageRemoteHours = input.userId !== null
            ? roundToTwoDecimals(currentRemoteHours * 1.1 + Math.random() * 0.3)
            : null;
        var officeHoursPrevPeriodDiff = getPercentageDiff(previousOfficeHours, currentOfficeHours);
        var remoteHoursPrevPeriodDiff = getPercentageDiff(previousRemoteHours, currentRemoteHours);
        var officeHoursTeamAvgDiff = null;
        var remoteHoursTeamAvgDiff = null;
        if (input.userId !== null && teamAverageOfficeHours !== null) {
            officeHoursTeamAvgDiff = getPercentageDiff(teamAverageOfficeHours, currentOfficeHours);
            remoteHoursTeamAvgDiff = getPercentageDiff(teamAverageRemoteHours, currentRemoteHours);
        }
        return {
            period: period.date,
            previousPeriodLabel: (_c = prevPeriod === null || prevPeriod === void 0 ? void 0 : prevPeriod.date) !== null && _c !== void 0 ? _c : '',
            officeHours: roundToTwoDecimals(currentOfficeHours),
            remoteHours: roundToTwoDecimals(currentRemoteHours),
            previousOfficeHours: roundToTwoDecimals(previousOfficeHours),
            previousRemoteHours: roundToTwoDecimals(previousRemoteHours),
            teamAverageOfficeHours: teamAverageOfficeHours,
            teamAverageRemoteHours: teamAverageRemoteHours,
            officeHoursPrevPeriodDiff: officeHoursPrevPeriodDiff,
            remoteHoursPrevPeriodDiff: remoteHoursPrevPeriodDiff,
            officeHoursTeamAvgDiff: officeHoursTeamAvgDiff,
            remoteHoursTeamAvgDiff: remoteHoursTeamAvgDiff
        };
    });
    var totalsHours = periods.reduce(function (acc, p) { return ({
        officeHours: acc.officeHours + p.officeHours,
        remoteHours: acc.remoteHours + p.remoteHours
    }); }, { officeHours: 0, remoteHours: 0 });
    var totalsPreviousHours = periods.reduce(function (acc, p) { return ({
        officeHours: acc.officeHours + p.previousOfficeHours,
        remoteHours: acc.remoteHours + p.previousRemoteHours
    }); }, { officeHours: 0, remoteHours: 0 });
    var totals = {
        officeHours: roundToTwoDecimals(totalsHours.officeHours),
        remoteHours: roundToTwoDecimals(totalsHours.remoteHours),
        officeHoursPrevPeriodDiff: getPercentageDiff(totalsPreviousHours.officeHours, totalsHours.officeHours),
        remoteHoursPrevPeriodDiff: getPercentageDiff(totalsPreviousHours.remoteHours, totalsHours.remoteHours),
        officeHoursTeamAvgDiff: input.userId !== null
            ? getPercentageDiff(roundToTwoDecimals(periods.reduce(function (sum, period) { var _a; return sum + ((_a = period.teamAverageOfficeHours) !== null && _a !== void 0 ? _a : 0); }, 0)), totalsHours.officeHours, {
                comparisonValueFormat: diffComparisonValueFormat.durationHours,
                comparisonType: diffComparisonType.teamAverage
            })
            : null,
        remoteHoursTeamAvgDiff: input.userId !== null
            ? getPercentageDiff(roundToTwoDecimals(periods.reduce(function (sum, period) { var _a; return sum + ((_a = period.teamAverageRemoteHours) !== null && _a !== void 0 ? _a : 0); }, 0)), totalsHours.remoteHours, {
                comparisonValueFormat: diffComparisonValueFormat.durationHours,
                comparisonType: diffComparisonType.teamAverage
            })
            : null
    };
    return { periods: periods, totals: totals };
};
